<template>
  <div>
    <rest-list
      :payload="fetchEndpoint"
      @dataLoaded="setData($event)"
      v-if="show"
    >
      <template>
        <rest-list-column
          :title="$t('TemplateCampaignsList_TemplateName')"
          prop="title"
        >
          <template v-slot:default="scope">
            <router-link :to="rowClickRoute(scope.row)">
              {{ scope.rowValue(scope) }}
            </router-link>
          </template>
        </rest-list-column>
        <rest-list-column-enum
          prop="trigger_type"
          :title="$t('TemplateCampaignsList_TriggerType')"
          :enum="campaignTriggerTypeLabels"
          :sortable="false"
        />
        <rest-list-column-enum
          :enum="MessageTypeLabel"
          prop="message_type"
          :title="$t('TemplateCampaignsList_MessageType')"
          v-if="selectedColumns.message_type"
          :sortable="false"
        />
        <rest-list-column
          prop="campaign_template_last_used"
          :title="$t('TemplateCampaignsList_LastUsed')"
          :sortable="true"
          :filter-input="false"
          v-if="selectedColumns.last_used"
        >
          <template slot-scope="{ row }">
            {{
              formatDate(row.campaign_template_last_used, 'YYYY-MM-DD HH:mm')
            }}
          </template>
        </rest-list-column>
        <rest-list-column
          prop="create_time"
          :title="$t('TemplateCampaignsList_CreateTime')"
          :sortable="true"
          :filter-input="false"
          v-if="selectedColumns.create_time"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.create_time, 'YYYY-MM-DD HH:mm') }}
          </template>
        </rest-list-column>
        <rest-list-column-actions>
          <template slot="header" slot-scope="{}">
            <rest-list-column-settings
              storage-key="templatecampaign-list-columns"
              :title="$t('TemplateCampaignsList_Settings')"
              :columns="columns"
              v-model="selectedColumns"
            ></rest-list-column-settings>
          </template>
          <template slot-scope="scope">
            <rest-list-action
              icon="el-icon-s-promotion"
              :title="$t('TemplateCampaignsList_Action_CreateCampaign')"
              :click="() => createCampaign(scope.row)"
            />
            <rest-list-action
              v-if="$can('updateProjectItem') && $can('sendPushCampaign')"
              :route="actionEndpoint(scope.row, 'update')"
              icon="el-icon-edit-outline"
              :title="$t('RestList_Action_Update')"
            />
            <rest-list-action-view
              :title="$t('RestList_Action_ViewCampaign')"
              v-else
              :route="actionEndpoint(scope.row, 'view')"
            />
            <rest-list-action
              v-if="$can('createProjectItem')"
              :click="() => copyTemplate(scope.row)"
              icon="el-icon-document-copy"
              :title="$t('RestList_Action_Copy')"
            />
            <rest-list-action
              v-if="$can('createProjectItem')"
              :click="() => copyBetweenProjects(scope.row)"
              icon="el-icon-share"
              :title="$t('TemplateCampaignsList_Action_CopyBetweenProjects')"
            />
            <rest-list-action
              v-if="canPublishTemplate"
              :click="() => publishTemplate(scope.row)"
              icon="el-icon-upload"
              :title="$t('TemplateCampaignsList_Action_PublishTemplate')"
            />
            <rest-list-action-delete
              v-if="$isRole('admin') || $can('deleteProjectItem')"
              :endpoint="rowDeleteEndpoint(scope.row)"
              :name="scope.row.title"
            />
            <rest-list-action
              v-if="
                $can('createProjectItem') &&
                  $can('sendPushCampaign') &&
                  $hasFeature('newMxa') &&
                  $hasFeature('mxaMigrationTool') &&
                  scope.row.type === campaignTypeEnum.TYPE_WORKFLOW &&
                  scope.row.new_mxa === 0
              "
              :click="() => handleWorkflowMigrationClick(scope.row)"
              :title="$t('RestList_Action_Workflow_Migrate')"
              icon="el-icon-refresh"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>
    <form-section v-else>
      <div class="section-wrapper">
        <i class="Templates_NoTemplates--MainIcon main-icon el-icon-document" />
        <h3 class="section-title">
          {{ $t('TemplateCampaignsList_NoTemplates_Title') }}
        </h3>
        <p class="section-description">
          {{ $t('TemplateCampaignsList_NoTemplates_Desc') }}
        </p>
        <el-button
          icon="el-icon-circle-plus-outline"
          class="btn-create-template"
          @click="createTemplateCampaign"
        >
          {{ $t('Template_CampaignsPage_CreateTemplate_Btn') }}
        </el-button>
      </div>
    </form-section>
    <copy-template-dialog
      :key="key"
      :dialog-visible.sync="showDialog"
      :template-id="templateId"
    />
    <publish-template-dialog
      :campaign-model="campaignRowData"
      :dialog-visible.sync="showPublishDialog"
    />
    <workflow-migration-dialog
      v-if="showWorkflowMigrationDialog"
      :migration-campaign="campaignRowData"
    />
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListAction from '@/common/components/rest/RestListAction.vue'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum.vue'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings.vue'
import RestListActionView from '@/common/components/rest/RestListActionView.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import { campaignTriggerTypeLabels } from '@/modules/analytics/enum/CampaignTypeLabels'
import { channelLabels } from '@/modules/campaign-manage/enum/Labels'
import { MessageTypeLabel } from '@/common/enum/MessageTypeEnum'
import {
  projectEndpoint,
  projectRoute,
  customConfirm,
  projectTimezoneDate
} from '@/core/helpers'
import {
  $hasFeature,
  $hasPlatformChannel,
  $hasPlatformActiveAppForChannel
} from '@/core/utils/VueAcl'
import _ from 'lodash'
import moment from 'moment'
import CopyTemplateDialog from '@/modules/campaigns/components/CopyTemplateDialog'
import PublishTemplateDialog from '@/modules/campaigns/components/PublishTemplateDialog.vue'
import WorkflowMigrationDialog from '@/modules/campaigns/components/WorkflowMigrationDialog.vue'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'TemplateCampaignsTab',
  components: {
    CopyTemplateDialog,
    RestListActionView,
    RestListAction,
    FormSection,
    RestListColumnSettings,
    RestListColumnActions,
    RestListColumnEnum,
    RestListColumn,
    RestList,
    RestListActionDelete,
    PublishTemplateDialog,
    WorkflowMigrationDialog
  },
  data() {
    return {
      data: [],
      selectedColumns: {},
      campaignTriggerTypeLabels,
      MessageTypeLabel,
      show: true,
      showDialog: false,
      showPublishDialog: false,
      templateId: null,
      key: null,
      campaignRowData: {},
      campaignTypeEnum,
      showWorkflowMigrationDialog: false
    }
  },
  computed: {
    canPublishTemplate() {
      return (
        (this.$isRole('admin') || this.$isRole('admin-support')) &&
        this.$can('CampaignTemplate:PublisherAccess')
      )
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `campaigns`,
        params: {
          sort: '-last_modified,-id',
          status_type: 'is_campaign_template',
          expand:
            'deactivate_time,create_time,campaign_template_last_used,schedule,labels,message_type,trigger_type,send_type,active,status_type,status_canonical,status,type,is_open,start_time_ts,end_time_ts,timezone,last_modified,channels,automatically_pick_winner_schedule_status,automatically_pick_winner,promotion,seed_list_enabled,throttling,broadcast,target_authorised_devices,new_mxa'
        }
      })
    },
    columns() {
      return {
        message_type: {
          title: this.$t('TemplateCampaignsList_MessageType'),
          defaultValue: true,
          validation: true
        },
        last_used: {
          title: this.$t('TemplateCampaignsList_LastUsed'),
          defaultValue: true,
          validation: true
        },
        create_time: {
          title: this.$t('TemplateCampaignsList_CreateTime'),
          defaultValue: true,
          validation: true
        }
      }
    }
  },
  mounted() {
    EventBus.$on('workflowMigrationDialog', () => {
      this.showWorkflowMigrationDialog = false
    })
  },
  methods: {
    formatDate(time, format = 'YYYY-MM-DD', hasTimezone = true) {
      if (!time) {
        return '-'
      }
      if (!hasTimezone) {
        return this.$t('CampaignsList_Label_UserTimezone', {
          date: moment
            .unix(time)
            .utc()
            .format(format)
        })
      }
      return projectTimezoneDate(time).format(format)
    },
    setData(event) {
      this.data = event
      if (
        !this.data[0] &&
        (!this.$route.query.filters || this.$route.query.filters === '{}')
      ) {
        this.show = false
      }
    },
    createTemplateCampaign() {
      this.$router.push(projectRoute({ name: 'CreateTemplateCampaign' }))
    },
    rowClickRoute(row) {
      if (this.$can('updateProjectItem') && this.$can('sendPushCampaign')) {
        return this.actionEndpoint(row, 'update')
      } else {
        return this.actionEndpoint(row, 'view')
      }
    },
    actionEndpoint(row, action) {
      return `/push/campaign/template/${action}?id=${row.id}&project_id=${this.$route.query.project_id}`
    },
    createCampaign(row) {
      const channelsWithErrors = []
      const disabledChannels = []

      Object.keys(channelLabels).forEach(c => {
        if (row.channels.includes(c)) {
          if (!$hasPlatformActiveAppForChannel(c)) {
            channelsWithErrors.push(c)
          }
          if (
            !channelsWithErrors.includes(c) &&
            (!($hasFeature(c) || $hasFeature(_.camelCase(c))) ||
              !$hasPlatformChannel(c))
          ) {
            disabledChannels.push(c)
          }
        }
      })

      if (disabledChannels.length + channelsWithErrors.length > 0) {
        let messages = []

        disabledChannels.forEach(c => {
          messages.push(
            this.$createElement(
              'div',
              { class: 'channel-problem-message' },
              this.$t('ChannelEnablement_ChannelNotAvailable', {
                c: this.$t('ChannelEnablement_Channel_' + c)
              })
            )
          )
        })

        channelsWithErrors.forEach(c => {
          messages.push(
            this.$createElement(
              'div',
              { class: 'channel-problem-message' },
              this.$t('ChannelEnablement_ChannelWithError', {
                c: this.$t('ChannelEnablement_Channel_' + c)
              })
            )
          )
        })

        customConfirm(
          this.$t('ChannelEnablement_ProblemsWithChannels'),
          messages,
          undefined,
          undefined,
          undefined,
          ' channels-alert'
        )
          .then(_ => this.$router.push(this.actionEndpoint(row, 'create-from')))
          .catch(_ => {})
      } else {
        this.$router.push(this.actionEndpoint(row, 'create-from'))
      }
    },
    copyTemplate(row) {
      this.$router.push(this.actionEndpoint(row, 'copy'))
    },
    copyBetweenProjects(row) {
      this.templateId = row.id
      this.showDialog = true
    },
    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `campaigns/${row.id}`
      })
    },
    publishTemplate(row) {
      this.campaignRowData = row
      this.showPublishDialog = true
    },
    handleWorkflowMigrationClick(row) {
      this.campaignRowData = row
      this.showWorkflowMigrationDialog = true
    }
  }
}
</script>

<style scoped>
.section-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 75vh;
}
.section-image {
  margin-bottom: 16px;
}
.section-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  margin-bottom: 8px;
  text-align: center;
  color: #5c6374;
}
.section-description {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #7e8596;
}
.btn-create-template {
  border-color: #1d87c9;
  color: #1d87c9;
  margin-top: 31px;
}

.Templates_NoTemplates--MainIcon {
  font-size: 46px;
  color: #1d87c9;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/TemplateCampaignsTab.vue